import { type Permission } from '@up2stay/up2promo-api-client';
import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  type PermissionCheck,
  usePermissions,
} from '@/core/hooks/use-permissions';

type CanProps = {
  children: ReactNode;
  fallback?: ReactNode;
  mode?: 'hide' | 'disable';
  permission?: PermissionCheck;
  anyOf?: PermissionCheck[];
  allOf?: PermissionCheck[];
};

function isAllowed(
  can: (permission: PermissionCheck) => boolean,
  canAny: (permissions: PermissionCheck[]) => boolean,
  canAll: (permissions: PermissionCheck[]) => boolean,
  props: Pick<CanProps, 'permission' | 'anyOf' | 'allOf'>,
): boolean {
  if (props.permission) return can(props.permission);
  if (props.anyOf?.length) return canAny(props.anyOf);
  if (props.allOf?.length) return canAll(props.allOf);
  return true;
}

function withDisabledChild(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const element = child as ReactElement<{ disabled?: boolean }>;
    return cloneElement(element, {
      ...element.props,
      disabled: true,
    });
  });
}

export function Can({
  children,
  fallback = null,
  mode = 'hide',
  permission,
  anyOf,
  allOf,
}: CanProps) {
  const { can, canAny, canAll, isLoading } = usePermissions();

  if (isLoading) {
    return <>{fallback}</>;
  }

  const allowed = isAllowed(can, canAny, canAll, { permission, anyOf, allOf });

  if (!allowed) {
    return <>{fallback}</>;
  }

  if (mode === 'disable') {
    return <>{withDisabledChild(children)}</>;
  }

  return <>{children}</>;
}

export type { Permission };
